import { PlanBoardAPI } from 'api/plan-board';
import { buildPhaseOptimisticPatch, getPhaseFromCollection, getPhaseProjectId, hasPhasePatchContent, isPhasePatchNoop, phaseCollection } from 'collections/phase';
import { getProjectFromCollection, projectCollection } from 'collections/project';
import { taskCollection } from 'collections/tasks';
import { logger } from 'services';
import { refreshPlanBoard, refreshPlanLanesForPhase } from './fetchers';
import { broadcastPlanLaneMove, broadcastPlanLaneRefresh, broadcastPlanLaneReorder } from './tab-sync';
import { planFunnel } from './telemetry';
import { applyPlanEntityMoveTokens, applyPlanEntityReorderTokens, getPlanFiltersHash, planBoardPhaseCollection, planLaneCollection } from './plan-lane-collection';
/**
 * WRITE layer for the paginated plan board.
 *
 * Verify-then-skip refetch policy: optimistic patches (laneBus view order +
 * the collection patches below) already put the client in the state the
 * server is expected to confirm. On settle we VERIFY the server's
 * `EntityReorderResult` against the optimistic state and only refetch —
 * fire-and-forget, never blocking the caller — when they disagree (stale
 * anchor degraded server-side, a concurrent edit, a filtered/partial window
 * we can't verify). On failure the same background refetch is the rollback.
 * Errors are logged with full context and rethrown so the calling hook can
 * surface a user-facing toast (never swallowed).
 */ const DEFAULT_FILTERS_HASH = getPlanFiltersHash(undefined);
/**
 * Does the optimistic lane state agree with the server's resolved position?
 * Only unfiltered lanes are position-verifiable (their token window is a
 * prefix of the canonical order, so window index == canonical index);
 * filtered lanes verify membership only. A token beyond the loaded window
 * (deep-lane BOTTOM move) fails the index check → background refetch.
 */ const lanesMatchResolvedPosition = (projectId, phaseId, entityId, resolvedPosition)=>{
    const lanes = planLaneCollection.getByIndex('byPhase', phaseId).filter((lane)=>lane.projectId === projectId);
    return lanes.every((lane)=>{
        const index = lane.pageTokens.indexOf(entityId);
        if (lane.filtersHash !== DEFAULT_FILTERS_HASH) {
            return index !== -1;
        }
        return resolvedPosition != null && index === resolvedPosition;
    });
};
/** Anchor-based in-lane reorder. */ export const reorderLaneEntity = async (projectId, phaseId, payload)=>{
    planFunnel.reorderStart({
        phaseId,
        entityId: payload.entityId
    });
    try {
        const result = await PlanBoardAPI.reorderEntity(phaseId, payload);
        if (!lanesMatchResolvedPosition(projectId, phaseId, payload.entityId, result.resolvedPosition)) {
            // Server resolved a different order than the optimistic one —
            // reconcile in the background (the refresh helper never rejects).
            planFunnel.reorderReconcile({
                phaseId,
                entityId: payload.entityId
            });
            void refreshPlanLanesForPhase(projectId, phaseId);
            // Peers applied the optimistic order too, so they are wrong in exactly the
            // same way. Now that the server HAS the change, a refresh signal is safe
            // and converges them on truth.
            broadcastPlanLaneRefresh(projectId, phaseId);
        } else {
            planFunnel.reorderVerified({
                phaseId,
                entityId: payload.entityId
            });
        }
        return result;
    } catch (error) {
        logger.error('plan-board reorderLaneEntity failed', {
            error,
            projectId,
            phaseId,
            payload
        });
        planFunnel.reorderError({
            phaseId,
            entityId: payload.entityId
        });
        // Roll the optimistic laneBus order back to server truth — in the
        // background; the caller only needs the rejection for its toast.
        void refreshPlanLanesForPhase(projectId, phaseId);
        // Retract the optimistic reorder on peers as well. Without this a rejected
        // drag stays visible in every other tab permanently — they have no
        // transaction of their own to roll back.
        broadcastPlanLaneRefresh(projectId, phaseId);
        throw error;
    }
};
/**
 * Optimistically rewrite each loaded lane's pageTokens to the dropped in-lane
 * order so reorderLaneEntity's resolve-position check agrees with the view.
 * Without this, an agreed reorder still triggers a background reconcile
 * because pageTokens stayed at the pre-drag order (SOURCE BUG #134).
 */ export const reorderPlanEntityTokensOptimistic = (projectId, phaseId, newOrder)=>{
    applyPlanEntityReorderTokens(projectId, phaseId, newOrder);
    // Peers hear the order the moment it is dropped, not a round-trip later.
    // Before this, an in-lane reorder reached other tabs on NO path at all: the
    // lane patch is `source: 'local'` (never broadcast) and, unlike a cross-phase
    // move, nothing on the task entity changes — so there was no entity write for
    // the sync adapter to carry and no refresh signal on the success path either.
    // A card dragged in one tab simply stayed put in every other one until
    // something else happened to refetch that lane.
    broadcastPlanLaneReorder(projectId, phaseId, newOrder);
};
/**
 * Optimistically insert a newly-created entity's token into every loaded lane
 * of its phase, so the card appears in the paginated lane the instant the
 * create resolves — without waiting on the (slow) batch refetch. Lanes render
 * by joining `pageTokens` live against `taskCollection`; a create only pushes
 * the task INTO `taskCollection`, so the token is absent from the lane record
 * and the card is invisible until a refetch returns fresh tokens (the "task
 * vanishes after create, reappears on batch" bug). Dedup-safe: strips the token
 * first, then inserts at the requested end; lane + board counts bump only when
 * the token is genuinely new to a lane (re-applying must not double-count).
 */ export const insertPlanEntityTokenOptimistic = (projectId, phaseId, entityId, position = 'bottom')=>{
    const lanes = planLaneCollection.getByIndex('byPhase', phaseId).filter((lane)=>lane.projectId === projectId);
    // True when the token was NOT already in at least one loaded lane — the
    // signal that this is a real insert (and that the counts should bump).
    const wasNew = lanes.length === 0 || lanes.some((lane)=>!lane.pageTokens.includes(entityId));
    if (lanes.length > 0) {
        planLaneCollection.push(lanes.map((lane)=>{
            const alreadyPresent = lane.pageTokens.includes(entityId);
            const tokens = lane.pageTokens.filter((token)=>token !== entityId);
            if (position === 'top') {
                tokens.unshift(entityId);
            } else {
                tokens.push(entityId);
            }
            return {
                ...lane,
                pageTokens: tokens,
                totalCount: alreadyPresent ? lane.totalCount : lane.totalCount + 1,
                filteredCount: alreadyPresent ? lane.filteredCount : lane.filteredCount + 1
            };
        }), {
            source: 'local'
        });
    }
    // Keep the board header count / ghost sizing coherent without a board GET.
    const boardPhase = planBoardPhaseCollection.get(phaseId);
    if (boardPhase && wasNew) {
        planBoardPhaseCollection.push([
            {
                ...boardPhase,
                entityCount: boardPhase.entityCount + 1,
                filteredEntityCount: boardPhase.filteredEntityCount != null ? boardPhase.filteredEntityCount + 1 : boardPhase.filteredEntityCount
            }
        ], {
            source: 'local'
        });
    }
};
/**
 * Optimistically move `entityId`'s token between the two phases' lane
 * records. Lanes render by joining `pageTokens` live against
 * `taskCollection`, so the laneBus (view-state) order alone can't make the
 * card appear in the destination lane — the moved token isn't in that lane's
 * record, so `useLaneEntities` never hands the destination its task. Patch
 * the records themselves: remove from every source-phase lane, insert into
 * every destination-phase lane at the anchor-resolved slot, and shift the
 * lane + board counts. Verification on settle (or the failure rollback)
 * triggers a background refetch when the server disagrees, so a mid-window
 * BOTTOM approximation or a rejected move self-corrects.
 */ export const movePlanEntityTokensOptimistic = (projectId, destPhaseId, payload)=>{
    const { entityId, fromPhaseId } = payload;
    // Lanes, the task's own phase, and the board counts — everything a receiving
    // tab can derive from the payload alone. Extracted so the cross-tab move
    // signal applies the identical logic (see `applyPlanEntityMoveTokens`).
    const { undo: undoEntityPhase } = applyPlanEntityMoveTokens(projectId, destPhaseId, payload);
    // Keep the project detail's phase entityOrder coherent too — paginated
    // lanes don't render from it, but the gantt and the date-shift math still
    // read it. Dedup-safe: strip the token from BOTH phases' orders first, then
    // insert at the anchor-resolved slot in the destination (re-applying an
    // order a caller already patched resolves to the same final state).
    const project = getProjectFromCollection(projectId);
    if (project === null || project === void 0 ? void 0 : project.projectPhases) {
        const projectPhases = project.projectPhases.map((phase)=>{
            var _phase_entityOrder;
            if (phase.projectPhaseId !== fromPhaseId && phase.projectPhaseId !== destPhaseId) {
                return phase;
            }
            const order = ((_phase_entityOrder = phase.entityOrder) !== null && _phase_entityOrder !== void 0 ? _phase_entityOrder : []).filter((token)=>token !== entityId);
            if (phase.projectPhaseId === destPhaseId) {
                const afterIndex = payload.afterEntityId ? order.indexOf(payload.afterEntityId) : -1;
                const beforeIndex = payload.beforeEntityId ? order.indexOf(payload.beforeEntityId) : -1;
                const insertAt = afterIndex !== -1 ? afterIndex + 1 : beforeIndex !== -1 ? beforeIndex : payload.anchor === 'TOP' ? 0 : order.length;
                order.splice(insertAt, 0, entityId);
            }
            return {
                ...phase,
                entityOrder: order
            };
        });
        projectCollection.push([
            {
                ...project,
                projectPhases
            }
        ], {
            source: 'mutation'
        });
    }
    // Only the entity patch is returned. The lane, project-order and board
    // counts are all restored by the caller's `refreshBothInBackground()` — the
    // task's own phase is the one piece no refetch of the plan board touches.
    return undoEntityPhase;
};
/**
 * Patch a phase's editable fields in BOTH cached homes — `phaseCollection`
 * (the canonical bean, state-db-migration-phase.md) and the phase inside the
 * project detail (`project.projectPhases`, still read directly by consumers
 * not yet flipped to `usePhase`/`usePhasesByProject`) — so a phase edit
 * (modal save, header status pill) paints immediately instead of waiting for
 * the API + detail refetch. Also used with the server response on settle,
 * making the eventual state authoritative. Undefined payload fields are
 * skipped. `buildPhaseOptimisticPatch`/`isPhasePatchNoop` (shared with
 * `updatePhaseOptimistic`) do the whitelisted-field work — no duplicate patch
 * logic here anymore.
 *
 * The `phaseCollection` write is tagged `source: 'mutation'` (not `'local'`)
 * deliberately: unlike lane-token patches, a phase field/date/status edit is
 * exactly the kind of change `attachPhaseTabSync` should propagate — this is
 * the cross-tab gap the migration doc calls out (phase edits had NO cross-tab
 * signal before `phaseCollection` existed).
 */ export const patchProjectPhaseInStores = (projectId, phasePatch, options = {})=>{
    var _project_projectPhases;
    const phaseId = phasePatch.projectPhaseId;
    if (phaseId == null || !hasPhasePatchContent(phasePatch)) {
        return;
    }
    const phase = getPhaseFromCollection(phaseId);
    if (phase && getPhaseProjectId(phase) === projectId && !isPhasePatchNoop(phase, phasePatch)) {
        phaseCollection.push([
            {
                ...phase,
                ...buildPhaseOptimisticPatch(phase, phasePatch)
            }
        ], {
            source: 'mutation'
        });
    }
    if (options.targets === 'board-only') {
        return;
    }
    const project = getProjectFromCollection(projectId);
    const detailPhase = project === null || project === void 0 ? void 0 : (_project_projectPhases = project.projectPhases) === null || _project_projectPhases === void 0 ? void 0 : _project_projectPhases.find((p)=>p.projectPhaseId === phaseId);
    if (project && detailPhase && !isPhasePatchNoop(detailPhase, phasePatch)) {
        projectCollection.push([
            {
                ...project,
                projectPhases: project.projectPhases.map((p)=>p.projectPhaseId === phaseId ? {
                        ...p,
                        ...buildPhaseOptimisticPatch(p, phasePatch)
                    } : p)
            }
        ], {
            source: 'mutation'
        });
    }
};
/**
 * Optimistically reorder the project's phases to `phaseOrder`. Plan lanes
 * render phase order straight off `project.projectPhases` array order
 * (project-plan.tsx maps it to `projectPhaseIds`), so reordering the array
 * repaints the lanes in the new order in the same tick — no API wait. Phases
 * not named in `phaseOrder` keep their existing relative order at the tail
 * (defensive; the modal always passes every phase). Returns a rollback thunk
 * that restores the prior phases onto the CURRENT project object, so
 * concurrent changes to other (non-phase) project fields survive the revert.
 */ export const reorderProjectPhasesOptimistic = (projectId, phaseOrder)=>{
    var _project_projectPhases;
    const project = getProjectFromCollection(projectId);
    if (!(project === null || project === void 0 ? void 0 : (_project_projectPhases = project.projectPhases) === null || _project_projectPhases === void 0 ? void 0 : _project_projectPhases.length)) {
        return ()=>undefined;
    }
    const previousPhases = project.projectPhases;
    const byId = new Map(previousPhases.map((phase)=>[
            phase.projectPhaseId,
            phase
        ]));
    const orderSet = new Set(phaseOrder);
    const reordered = [
        ...phaseOrder.map((id)=>byId.get(id)).filter((phase)=>phase != null),
        ...previousPhases.filter((phase)=>!orderSet.has(phase.projectPhaseId))
    ];
    projectCollection.push([
        {
            ...project,
            projectPhases: reordered
        }
    ], {
        source: 'mutation'
    });
    return ()=>{
        const current = getProjectFromCollection(projectId);
        if (current) {
            projectCollection.push([
                {
                    ...current,
                    projectPhases: previousPhases
                }
            ], {
                source: 'mutation'
            });
        }
    };
};
/**
 * Marking a phase private marks every task in it private (server cascade in
 * ProjectPhaseService.updatePhasePrivacy). Mirror that on the LOADED tasks so
 * cards flip their privacy immediately with the optimistic phase patch —
 * unloaded window pages get server truth when fetched. Shared (un-private)
 * has NO server cascade, so only the private direction is mirrored; callers
 * refresh the phase's lanes on settle for the authoritative task state.
 * Returns the number of loaded tasks flipped (for funnel telemetry).
 */ export const cascadePhasePrivacyToLoadedTasks = (phaseId)=>{
    const tasksToPatch = taskCollection.getAll().filter((task)=>{
        var _task_projectPhase;
        return ((_task_projectPhase = task.projectPhase) === null || _task_projectPhase === void 0 ? void 0 : _task_projectPhase.projectPhaseId) === phaseId && task.private !== true;
    });
    if (tasksToPatch.length > 0) {
        taskCollection.push(tasksToPatch.map((task)=>({
                ...task,
                private: true
            })), {
            source: 'mutation'
        });
    }
    return tasksToPatch.length;
};
/** Cross-phase move — verify-then-skip refetch, never blocking the caller. */ export const movePlanEntity = async (projectId, destPhaseId, payload)=>{
    const refreshBothInBackground = ()=>{
        void Promise.all([
            refreshPlanLanesForPhase(projectId, payload.fromPhaseId),
            refreshPlanLanesForPhase(projectId, destPhaseId),
            refreshPlanBoard(projectId)
        ]).catch(()=>undefined);
    };
    /**
   * Tell peers to re-read both lanes — the CONVERGENCE path, not the happy one.
   *
   * A refresh cannot be sent optimistically: a peer that re-reads while the
   * server still reports the old membership sees the old membership and undoes
   * itself. So this is only for after the server has spoken, and only when it
   * disagreed with the optimistic result or rejected it outright. On the verified
   * path peers already hold the right answer from `broadcastPlanLaneMove` and a
   * refresh here would make every one of them fetch to learn what it was told a
   * moment ago.
   */ const broadcastBothLanes = ()=>{
        broadcastPlanLaneRefresh(projectId, payload.fromPhaseId);
        broadcastPlanLaneRefresh(projectId, destPhaseId);
    };
    planFunnel.moveStart({
        entityId: payload.entityId,
        fromPhaseId: payload.fromPhaseId,
        toPhaseId: destPhaseId
    });
    // Token-level optimistic move so the card renders in the destination lane
    // immediately (see movePlanEntityTokensOptimistic).
    const undoEntityPhase = movePlanEntityTokensOptimistic(projectId, destPhaseId, payload);
    // Peers re-file the card from these anchors immediately — no fetch, and no
    // waiting on the round-trip. Previously they learned nothing until the move
    // persisted and then had to fetch BOTH lanes to find out what happened.
    broadcastPlanLaneMove(projectId, destPhaseId, payload);
    planFunnel.moveOptimistic({
        entityId: payload.entityId,
        toPhaseId: destPhaseId
    });
    try {
        const result = await PlanBoardAPI.moveEntity(projectId, destPhaseId, payload);
        const sourceClean = planLaneCollection.getByIndex('byPhase', payload.fromPhaseId).filter((lane)=>lane.projectId === projectId).every((lane)=>!lane.pageTokens.includes(payload.entityId));
        const verified = result.projectPhaseId === destPhaseId && sourceClean && lanesMatchResolvedPosition(projectId, destPhaseId, payload.entityId, result.resolvedPosition);
        if (!verified) {
            planFunnel.moveReconcile({
                entityId: payload.entityId,
                toPhaseId: destPhaseId,
                reason: result.projectPhaseId !== destPhaseId ? 'phase_mismatch' : !sourceClean ? 'source_dirty' : 'position_mismatch'
            });
            refreshBothInBackground();
            // Peers applied the same optimistic move, so they are wrong the same way.
            // Now that the server has spoken, a refresh converges them.
            broadcastBothLanes();
        } else {
            planFunnel.moveVerified({
                entityId: payload.entityId,
                toPhaseId: destPhaseId
            });
        // Deliberately NO refresh broadcast. The optimistic move signal already
        // put every peer in the verified state; telling them to re-read would
        // trade a free, already-applied update for a fetch per peer per lane.
        }
        return result;
    } catch (error) {
        logger.error('plan-board movePlanEntity failed', {
            error,
            projectId,
            destPhaseId,
            payload
        });
        planFunnel.moveError({
            entityId: payload.entityId,
            toPhaseId: destPhaseId
        });
        // The task never left its phase. Restore that BEFORE the lane refetch:
        // the reconciler derives token membership from `projectPhase`, so a task
        // still claiming the destination would have the refetched source token
        // stripped straight back out again.
        undoEntityPhase();
        // Peers applied the whole optimistic move, `projectPhase` included, and a
        // refresh alone cannot undo that: it restores the source TOKEN, then
        // `reconcilePhaseTokens` reads the still-destination `projectPhase` and
        // re-files the card to the destination — so a refused drag stuck there on
        // every other tab, with header counts off by one.
        //
        // Sending the INVERSE move retracts exactly what was applied: same applier,
        // phases swapped, so the token, the entity's phase and both counts all come
        // back. The refresh that follows only has to settle the ordering.
        broadcastPlanLaneMove(projectId, payload.fromPhaseId, {
            ...payload,
            fromPhaseId: destPhaseId
        });
        // Rollback via background reconciliation — the caller only needs the
        // rejection for its toast. Peers are told too: any that already refetched
        // (or optimistically moved the token themselves) must return to the
        // server's view.
        refreshBothInBackground();
        broadcastBothLanes();
        throw error;
    }
};
